import {createSlice, createAsyncThunk, PayloadAction} from '@reduxjs/toolkit';
import {supabase} from '../../api/supabase'; // Corrected path
import type {RootState} from '../store'; // Adjusted path
import {sendDMNotification} from '../../api/dmNotifications';

// Interfaces
export interface Profile {
  id: string;
  full_name: string;
  avatar_url: string | null;
}

export interface DirectMessageContent {
  id: string;
  dm_id: string; // Foreign key to DirectMessageChannel
  sender_id: string;
  content: string;
  created_at: string;
  profile?: Profile; // Sender's profile
}

export interface DirectMessageChannel {
  id: string; // This is the dm_id
  otherUserId: string;
  otherUserName: string;
  avatarUrl: string | null;
  last_message_at?: string; // For sorting or display
  latest_message?: string; // Latest message content
  latest_message_sender_id?: string; // Who sent the latest message
  created_at: string; // When the match was created
  isGoldenMatch?: boolean; // Whether this is a Golden Balloon match
  daysRemaining?: number | null; // Days remaining for Golden Balloon matches
  expiresAt?: string; // When Golden Balloon match expires
  profileUnavailable?: boolean; // Flag indicating profile data is using fallback
}

interface DirectMessageState {
  dmChannels: DirectMessageChannel[];
  currentDMMessages: DirectMessageContent[];
  currentDMId: string | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null | undefined;
}

const initialState: DirectMessageState = {
  dmChannels: [],
  currentDMMessages: [],
  currentDMId: null,
  status: 'idle',
  error: null,
};

// Define a simple type for the raw DM data from Supabase
interface RawDMChannelData {
  id: string;
  user1_id: string;
  user2_id: string;
  last_message_at?: string;
  created_at: string;
}

/**
 * Creates a fallback profile for users whose profile data cannot be fetched
 * @param userId - The user ID for the fallback profile
 * @returns A profile object with default values
 */
const createFallbackProfile = (userId: string): {
  id: string;
  full_name: string;
  photos: null;
} => ({
  id: userId,
  full_name: 'User',
  photos: null,
});

// Timeout duration for queries (30 seconds)
const QUERY_TIMEOUT_MS = 30000;

/**
 * Creates a promise that rejects after a specified timeout
 * @param ms - Timeout duration in milliseconds
 * @returns A promise that rejects with a timeout error
 */
const createTimeoutPromise = (ms: number): Promise<never> => {
  return new Promise((_, reject) => {
    setTimeout(() => {
      reject(new Error(`Query timeout after ${ms / 1000} seconds`));
    }, ms);
  });
};

// Async Thunks

// Fetch all DM channels for the current user
export const fetchDMChannels = createAsyncThunk(
  'directMessages/fetchChannels',
  async (_, {getState}) => {
    const state = getState() as RootState;
    const user = state.auth.user;
    if (!user) throw new Error('User not authenticated');

    // Wrap the query in a timeout
    const dmChannelsPromise = supabase
      .from('direct_messages')
      .select('id, user1_id, user2_id, last_message_at, created_at')
      .or(`user1_id.eq.${user.id},user2_id.eq.${user.id}`)
      .order('last_message_at', {ascending: false});

    const {data: dmChannelsData, error: dmError} = await Promise.race([
      dmChannelsPromise,
      createTimeoutPromise(QUERY_TIMEOUT_MS),
    ]).catch((err) => {
      if (err.message.includes('timeout')) {
        throw new Error('Request timed out. Please check your connection and try again.');
      }
      throw err;
    }) as any;

    if (dmError) throw dmError;
    if (!dmChannelsData) return [];

    const otherUserIds = (dmChannelsData as RawDMChannelData[])
      .map((dm: RawDMChannelData) =>
        dm.user1_id === user.id ? dm.user2_id : dm.user1_id,
      )
      .filter((id: string | null) => id !== null) as string[];

    if (otherUserIds.length === 0) return [];

    // Remove strict filtering - fetch all profiles regardless of status
    const profilesPromise = supabase
      .from('profiles')
      .select('id, full_name, photos')
      .in('id', otherUserIds);

    const {data: profiles, error: profilesError} = await Promise.race([
      profilesPromise,
      createTimeoutPromise(QUERY_TIMEOUT_MS),
    ]).catch((err) => {
      if (err.message.includes('timeout')) {
        throw new Error('Request timed out. Please check your connection and try again.');
      }
      throw err;
    }) as any;

    if (profilesError) {
      throw profilesError;
    }

    // Create profile map with fallback for missing profiles
    const profileMap = new Map(profiles?.map((p: any) => [p.id, p]));

    // Add fallback profiles for any missing user IDs
    otherUserIds.forEach((userId) => {
      if (!profileMap.has(userId)) {
        profileMap.set(userId, createFallbackProfile(userId));
      }
    });

    // Fetch Golden Balloon match information
    const {data: matchesData, error: matchesError} = await supabase
      .from('matches')
      .select('user1_id, user2_id, is_golden_match, expires_at')
      .or(`user1_id.eq.${user.id},user2_id.eq.${user.id}`)
      .eq('is_golden_match', true);

    // Create a map to quickly find Golden Balloon matches
    const goldenMatchMap = new Map();
    matchesData?.forEach((match: any) => {
      const otherUserId = match.user1_id === user.id ? match.user2_id : match.user1_id;
      goldenMatchMap.set(otherUserId, {
        isGoldenMatch: match.is_golden_match,
        expiresAt: match.expires_at,
      });
    });

    // Batch fetch all latest messages in a single query
    const dmIds = (dmChannelsData as RawDMChannelData[]).map((dm) => dm.id);

    const messagesPromise = supabase
      .from('direct_messages_content')
      .select('dm_id, content, sender_id, created_at')
      .in('dm_id', dmIds)
      .order('created_at', { ascending: false });

    const { data: allMessages } = await Promise.race([
      messagesPromise,
      createTimeoutPromise(QUERY_TIMEOUT_MS),
    ]).catch((err) => {
      return { data: [] };
    }) as any;

    // Create a map of dm_id to latest message
    const latestMessageMap = new Map();
    allMessages?.forEach((msg: any) => {
      if (!latestMessageMap.has(msg.dm_id)) {
        latestMessageMap.set(msg.dm_id, msg);
      }
    });

    // Map DM channels with all data
    const channelsWithMessages = (dmChannelsData as RawDMChannelData[]).map((dm: RawDMChannelData) => {
      try {
        const otherUserId = dm.user1_id === user.id ? dm.user2_id : dm.user1_id;
        const otherProfile: any = profileMap.get(otherUserId);
        const isUsingFallback = otherProfile?.full_name === 'User' && otherProfile?.photos === null;

        // Get main photo (first photo) instead of avatar_url
        let mainPhotoUrl = null;
        if (
          otherProfile?.photos &&
          Array.isArray(otherProfile.photos) &&
          otherProfile.photos.length > 0
        ) {
          const firstPhoto = otherProfile.photos[0];
          mainPhotoUrl =
            typeof firstPhoto === 'string' ? firstPhoto : firstPhoto?.uri;
        }

        // Get latest message from the batched results
        const latestMessage = latestMessageMap.get(dm.id);

        // Get Golden Balloon match info
        const goldenMatchInfo = goldenMatchMap.get(otherUserId);
        let daysRemaining = null;
        if (goldenMatchInfo?.expiresAt) {
          const expiresAt = new Date(goldenMatchInfo.expiresAt);
          const now = new Date();
          const diffTime = expiresAt.getTime() - now.getTime();
          daysRemaining = Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
        }

        return {
          id: dm.id,
          otherUserId: otherUserId,
          otherUserName: otherProfile?.full_name || 'User',
          avatarUrl: mainPhotoUrl,
          last_message_at: dm.last_message_at,
          latest_message: latestMessage?.content || null,
          latest_message_sender_id: latestMessage?.sender_id || null,
          created_at: dm.created_at,
          isGoldenMatch: goldenMatchInfo?.isGoldenMatch || false,
          daysRemaining: daysRemaining,
          expiresAt: goldenMatchInfo?.expiresAt,
          profileUnavailable: isUsingFallback,
        };
      } catch (error) {
        // Return a fallback channel object
        const otherUserId = dm.user1_id === user.id ? dm.user2_id : dm.user1_id;
        return {
          id: dm.id,
          otherUserId: otherUserId,
          otherUserName: 'User',
          avatarUrl: null,
          last_message_at: dm.last_message_at,
          latest_message: null,
          latest_message_sender_id: null,
          created_at: dm.created_at,
          isGoldenMatch: false,
          daysRemaining: null,
          expiresAt: undefined,
          profileUnavailable: true,
        };
      }
    });

    // Filter out DMs with oneself, but keep all other matches including those with fallback profiles
    const filteredChannels = channelsWithMessages.filter(
      (dm: DirectMessageChannel) => dm.otherUserId !== user.id,
    );

    return filteredChannels;
  },
);

// Fetch messages for a specific DM channel
export const fetchDMMessages = createAsyncThunk(
  'directMessages/fetchMessages',
  async (dmId: string, {dispatch}) => {
    dispatch(setCurrentDMId(dmId)); // Set current DM ID for state tracking
    const {data: messagesData, error: messagesError} = await supabase
      .from('direct_messages_content')
      .select(
        `
        *,
        profile:sender_id (
          full_name,
          photos
        )
      `,
      )
      .eq('dm_id', dmId)
      .order('created_at', {ascending: true});

    if (messagesError) throw messagesError;

    // Process messages to use main photo instead of avatar_url
    const processedMessages = (messagesData || []).map((message: any) => {
      if (
        message.profile &&
        message.profile.photos &&
        Array.isArray(message.profile.photos) &&
        message.profile.photos.length > 0
      ) {
        const firstPhoto = message.profile.photos[0];
        const mainPhotoUrl =
          typeof firstPhoto === 'string' ? firstPhoto : firstPhoto?.uri;
        return {
          ...message,
          profile: {
            ...message.profile,
            avatar_url: mainPhotoUrl,
          },
        };
      }
      return {
        ...message,
        profile: {
          ...message.profile,
          avatar_url: null,
        },
      };
    });

    return processedMessages as DirectMessageContent[];
  },
);

// Send a direct message
export const sendDirectMessage = createAsyncThunk(
  'directMessages/sendMessage',
  async ({dmId, content}: {dmId: string; content: string}, {getState}) => {
    const state = getState() as RootState;
    const user = state.auth.user;
    if (!user) throw new Error('User not authenticated');

    const {data: newMessage, error} = await supabase
      .from('direct_messages_content')
      .insert({
        dm_id: dmId,
        sender_id: user.id,
        content: content,
      })
      .select(
        `
        *,
        profile:sender_id (
          full_name,
          photos
        )
      `,
      )
      .single();

    if (error) throw error;

    // Update last_message_at for the DM channel
    await supabase
      .from('direct_messages')
      .update({last_message_at: new Date().toISOString()})
      .eq('id', dmId);

    // Get DM info to find the other user
    const {data: dmData} = await supabase
      .from('direct_messages')
      .select('user1_id, user2_id')
      .eq('id', dmId)
      .single();

    if (dmData) {
      const otherUserId = dmData.user1_id === user.id ? dmData.user2_id : dmData.user1_id;

      // Get current user's profile for sender name
      const {data: currentUserProfile} = await supabase
        .from('profiles')
        .select('full_name')
        .eq('id', user.id)
        .single();

      // Send DM notification to the other user
      if (otherUserId && currentUserProfile?.full_name) {
        try {
          await sendDMNotification(
            otherUserId,
            currentUserProfile.full_name,
            content
          );
        } catch (notificationError) {
          // Don't throw here - message was sent successfully even if notification failed
        }
      }
    }

    // Process the new message to use main photo instead of avatar_url
    let processedMessage = newMessage as any;
    if (
      processedMessage.profile &&
      processedMessage.profile.photos &&
      Array.isArray(processedMessage.profile.photos) &&
      processedMessage.profile.photos.length > 0
    ) {
      const firstPhoto = processedMessage.profile.photos[0];
      const mainPhotoUrl =
        typeof firstPhoto === 'string' ? firstPhoto : firstPhoto?.uri;
      processedMessage = {
        ...processedMessage,
        profile: {
          ...processedMessage.profile,
          avatar_url: mainPhotoUrl,
        },
      };
    } else {
      processedMessage = {
        ...processedMessage,
        profile: {
          ...processedMessage.profile,
          avatar_url: null,
        },
      };
    }

    return processedMessage as DirectMessageContent;
  },
);

// Unmatch a user (delete DM channel and messages)
export const unmatchUser = createAsyncThunk(
  'directMessages/unmatchUser',
  async (
    {dmId, otherUserId}: {dmId: string; otherUserId: string},
    {getState, dispatch},
  ) => {
    const state = getState() as RootState;
    const currentUserId = state.auth.user?.id;
    if (!currentUserId) throw new Error('User not authenticated');

    // 1. Delete messages from direct_messages_content
    const {error: messagesError} = await supabase
      .from('direct_messages_content')
      .delete()
      .eq('dm_id', dmId);
    if (messagesError) throw messagesError;

    // 2. Delete the direct_message (match) record
    const {error: dmError} = await supabase
      .from('direct_messages')
      .delete()
      .eq('id', dmId);
    if (dmError) throw dmError;

    // 3. Optionally, attempt to delete from a 'matches' table if it exists
    // This part is speculative based on ChatScreen.tsx and might need adjustment
    // For now, we assume the direct_messages table is the primary one for DMs.
    // If you have a separate 'matches' table that links users, you'd delete here too.
    // Example:
    // await supabase
    //   .from('matches')
    //   .delete()
    //   .or(`(user1_id.eq.${currentUserId},user2_id.eq.${otherUserId}),(user1_id.eq.${otherUserId},user2_id.eq.${currentUserId})`);

    // After successful unmatch, remove the channel from the local state
    dispatch(removeDMChannel(dmId));
    return dmId; // Return dmId to indicate success for this specific channel
  },
);

const directMessageSlice = createSlice({
  name: 'directMessages',
  initialState,
  reducers: {
    setCurrentDMId: (state, action: PayloadAction<string | null>) => {
      state.currentDMId = action.payload;
      if (action.payload === null) {
        state.currentDMMessages = []; // Clear messages when no DM is selected
      }
    },
    addDMMessage: (state, action: PayloadAction<DirectMessageContent>) => {
      // Add message only if it belongs to the currently active DM
      if (state.currentDMId === action.payload.dm_id) {
        const existingMessage = state.currentDMMessages.find(
          m => m.id === action.payload.id,
        );
        if (!existingMessage) {
          state.currentDMMessages.push(action.payload);
        }
      }
    },
    clearDMState: state => {
      state.dmChannels = [];
      state.currentDMMessages = [];
      state.currentDMId = null;
      state.status = 'idle';
      state.error = null;
    },
    removeDMChannel: (state, action: PayloadAction<string>) => {
      state.dmChannels = state.dmChannels.filter(
        channel => channel.id !== action.payload,
      );
      if (state.currentDMId === action.payload) {
        state.currentDMId = null;
        state.currentDMMessages = [];
      }
    },
  },
  extraReducers: builder => {
    builder
      // fetchDMChannels
      .addCase(fetchDMChannels.pending, state => {
        state.status = 'loading';
      })
      .addCase(fetchDMChannels.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.dmChannels = action.payload;
      })
      .addCase(fetchDMChannels.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      // fetchDMMessages
      .addCase(fetchDMMessages.pending, state => {
        state.status = 'loading';
      })
      .addCase(fetchDMMessages.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.currentDMMessages = action.payload;
      })
      .addCase(fetchDMMessages.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      // sendDirectMessage
      .addCase(sendDirectMessage.pending, state => {
        // Optionally set a specific sending status
      })
      .addCase(sendDirectMessage.fulfilled, (state, action) => {
        // The message is added optimistically or via subscription
        // If not, add it here:
        // if (state.currentDMId === action.payload.dm_id) {
        //   state.currentDMMessages.push(action.payload);
        // }
        // Also, update dmChannels last_message_at or re-sort, or re-fetch channels
        const channelIndex = state.dmChannels.findIndex(
          c => c.id === action.payload.dm_id,
        );
        if (channelIndex !== -1) {
          const channel = state.dmChannels[channelIndex];
          channel.last_message_at = action.payload.created_at;
          channel.latest_message = action.payload.content;
          channel.latest_message_sender_id = action.payload.sender_id;
          // Bring the channel to the top
          state.dmChannels.splice(channelIndex, 1);
          state.dmChannels.unshift(channel);
        }
      })
      .addCase(sendDirectMessage.rejected, (state, action) => {
        state.error = action.error.message; // Handle send error
      })
      // unmatchUser
      .addCase(unmatchUser.pending, state => {
        state.status = 'loading';
      })
      .addCase(unmatchUser.fulfilled, (state, action) => {
        state.status = 'succeeded';
        // Channel removal is handled by the `removeDMChannel` reducer,
        // which is dispatched from the thunk itself.
        // If currentDMId was the one unmatched, it's cleared by removeDMChannel.
      })
      .addCase(unmatchUser.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      });
  },
});

export const {setCurrentDMId, addDMMessage, clearDMState, removeDMChannel} =
  directMessageSlice.actions;
export default directMessageSlice.reducer;
